import { useIntl } from 'react-intl'
import { Column } from 'primereact/column'
import { useNavigate } from 'react-router-dom'
import { DataTableFun } from '@/utils/helpers/dataTableFun.js'
import auxFun from '@/utils/helpers/auxFun.js'
import useModal from '@/utils/hooks/useModal.js'
import PhotoCircle from '@/components/photos/PhotoCircle.jsx'
import Modal from '@/components/modal/Modal.jsx'
import UsersForm from '@/components/users/UsersForm.jsx'
import RolesDropDown from '@/components/roles/RolesDropDown.jsx'
import useInputs from '@/utils/hooks/useInputs.js'
import useAccess from '@/utils/hooks/useAccess.js'
import { GROUPS, MODULES } from '@/utils/constants/roles.js'
import { usersPathname } from '@/routes/settings/pathname.js'
import useHandlerBasicActions from '@/utils/hooks/useHandlerBasicActions.js'
import DataTableComp from '@/components/common/data-table/DataTableComp.jsx'
import ViewButton from '@/components/common/buttons/ViewButton.jsx'
import MainCommonView from '@/components/common/MainCommonView.jsx'
import UsersService from '@/services/users/users.service.js'
import './users.scss'
import CentersDropdown from '@/components/centers/CentersDropdown.jsx'

const Users = () => {
  const { formatMessage: msg } = useIntl()
  const navigate = useNavigate()

  const { isShowing, toggle } = useModal()
  const { inputs, onChangeInputs, updateInputs } = useInputs()
  const { canAdd, canDelete, canEdit } = useAccess()

  const dataFacade = useHandlerBasicActions({
    sortFieldInitial: 'date',
    sortInit: -1,
    getAll: ({ filter, limit, skip, sort }) =>
      UsersService.getAll({
        filter,
        limit,
        skip,
        sort,
      }),
  })

  const handleCreate = async () => {
    const body = {
      ...inputs,
      centerId: inputs?.center?._id || inputs?.center,
      center: inputs?.center?._id || inputs?.center,
    }
    const response = await dataFacade.create(body)
    if (response) {
      updateInputs({})
      dataFacade.reload()
      toggle()
    }
  }

  const onRowEditComplete = async ({ newData }) => {
    const body = {
      name: newData?.name,
      username: newData?.username,
      email: newData?.email,
      role: newData?.role?._id || newData?.role,
      centerId: newData?.center?._id || newData?.center,
      center: {
        _id: newData?.center?._id,
        basicData: { name: newData?.center?.basicData?.name },
      },
    }
    dataFacade.edit({ _id: newData?._id, body })
  }

  const roleValue = (options) => {
    return <span>{options?.role?.role}</span>
  }

  const rolesDropDown = (options) => {
    return (
      <RolesDropDown
        value={options?.rowData?.role}
        onChange={({ target: { value } }) => options.editorCallback(value)}
      />
    )
  }

  const centerDropDown = (options) => {
    return (
      <CentersDropdown
        value={options?.rowData?.center}
        onChange={({ target: { value } }) => options.editorCallback(value)}
      />
    )
  }

  const userBodyIMG = (image) => {
    return <PhotoCircle image={image} />
  }

  const goToPathButton = (target) => (
    <ViewButton
      onClick={() => navigate(`${usersPathname.USERS(target._id)}`)}
    />
  )

  const headerOptions = {
    title: msg({ id: 'users' }),
    handlerHeaderActions: {
      showBackButton: true,
      secondaryButton: canAdd(MODULES.USERS, GROUPS.BASIC_INFO) && toggle,
      secondaryButtonText: 'button.add',
      secondaryButtonIcon: 'pi-plus',
    },
    searchOptions: dataFacade?.searchOptions,
  }

  const pagerOptions = {
    ...dataFacade?.pagerOptions,
    totalRecords: dataFacade?.data?.total,
  }

  const dataTable = (
    <DataTableComp
      loading={dataFacade?.loading}
      pagerOptions={pagerOptions}
      tableOptions={{
        className: 'table-without-menu',
        value: dataFacade?.data?.resp,

        dataKey: '_id',
        editMode: 'row',
        onRowEditComplete,
        ...dataFacade?.sortOptions,
      }}
      columns={[
        <Column
          style={{ maxWidth: '60px' }}
          body={(rowData) => userBodyIMG(rowData?.image)}
        />,
        <Column
          field="name"
          sortable
          editor={(rowData) => DataTableFun.textEditor(rowData)}
          onCellEditComplete={DataTableFun.onCellEditComplete}
          header={msg({ id: 'name' })}
        />,
        <Column
          field="username"
          editor={(rowData) => DataTableFun.textEditor(rowData)}
          onCellEditComplete={DataTableFun.onCellEditComplete}
          header={msg({ id: 'user' })}
        />,
        <Column
          field="email"
          editor={(rowData) => DataTableFun.textEditor(rowData)}
          onCellEditComplete={DataTableFun.onCellEditComplete}
          header={msg({ id: 'email' })}
        />,
        <Column
          field="center"
          style={{ minWidth: '200px' }}
          body={(options) => options?.center?.basicData?.name}
          editor={(options) => centerDropDown(options)}
          onCellEditComplete={DataTableFun.onCellEditComplete}
          header={msg({ id: 'center' })}
        />,
        <Column
          field="role"
          body={(options) => roleValue(options)}
          editor={(options) => rolesDropDown(options)}
          onCellEditComplete={DataTableFun.onCellEditComplete}
          header={msg({ id: 'role' })}
        />,
        <Column
          field="createdAt"
          body={(rowData) => auxFun.parseDate(rowData.createdAt)}
          header={msg({ id: 'discharge.data' })}
        />,
        canDelete(MODULES.USERS, GROUPS.BASIC_INFO) && (
          <Column
            className="row-delete"
            align="right"
            body={(rowData) =>
              DataTableFun.buttonDelete(rowData, async () => {
                // Por algún motivo, aquí hace falta hacer esto para que se refresque la tabla
                await dataFacade.remove(rowData._id)
                dataFacade.reload()
              })
            }
          />
        ),
        canEdit(MODULES.USERS, GROUPS.BASIC_INFO) && (
          <Column className="row-edit" align="right" rowEditor />
        ),
        /*<Column body={(target) => goToPathButton(target)}/>*/
      ]}
    />
  )

  return (
    <div className="users-container">
      <MainCommonView
        headerOptions={headerOptions}
        componentData={dataTable}
        pagerOptions={pagerOptions}
      />

      <Modal
        isShowing={isShowing}
        hide={() => {
          toggle()
          updateInputs()
        }}
        width="90%"
        maxWidth="450px"
        title={msg({ id: 'user.add.title' })}
        context={
          <UsersForm
            inputs={inputs}
            handleSubmit={handleCreate}
            handleChange={onChangeInputs}
          />
        }
      />
    </div>
  )
}

export default Users
